import React, { useState, useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import classnames from 'classnames';
import { TrackingHandler } from 'client/tracking/handler';
import { TopRatedArticleMap } from 'client/tracking/maps/editorial/top-rated';
import { CmsEntities } from 'client/data/models/cms';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { HighlightVideo } from 'site-modules/editorial/components/highlight-video/highlight-video';
import { MainContent } from 'site-modules/editorial/components/top-rated/main-content/main-content';
import { Share } from 'site-modules/shared/components/share/share';
import { PhotoflipperImageTopRated } from 'site-modules/shared/components/editorial/photoflipper-image-top-rated/photoflipper-image-top-rated';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { buildArticlePageBreadcrumb } from 'site-modules/shared/utils/top-rated/top-rated';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';
import { getThemeToUse } from 'site-modules/shared/utils/top-rated/use-theme';
import { WinnerTilesNavArticle } from 'site-modules/editorial/components/top-rated/winner-tiles-nav/2021-2022/winner-tiles-nav-article';
import { HeroContent } from 'site-modules/editorial/components/hero-content/hero-content';
import { HERO_PHOTO_SIZE } from 'site-modules/shared/constants/top-rated/top-rated';
import { AwardsAccolades } from 'site-modules/editorial/components/top-rated/awards-accolades/2020-2022/awards-accolades';
import { rendererTopRatedArticle } from 'site-modules/editorial/pages/top-rated/top-rated-article-renderer';

import './styles/top-rated-article.scss';

export function TopRatedArticleUI({ content, pathname = null, isMobile = false, year, themeContent = null }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoModalData, setVideoModalData] = useState({});
  const themeToUse = getThemeToUse(themeContent);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TrackingHandler.useMap(TopRatedArticleMap);
  }, []);

  const onToggleVideoModal = useCallback(
    videolData => {
      topRatedHelpers.toggleBodyModalClass(themeToUse, !isVideoModalOpen);
      setIsVideoModalOpen(!isVideoModalOpen);

      if (!isVideoModalOpen) {
        setVideoModalData(videolData);
      }
    },
    [isVideoModalOpen, themeToUse]
  );

  const { published, updated, title } = content;
  const canonical = getCanonical(content);
  const mainEntries = content.child('main').children();

  // hero video
  const heroVideo = content.child('hero-content');
  const hasHeroVideo = !!heroVideo.metadata('videoId').value();
  const cssVideoProps = {};
  cssVideoProps['--object-position'] = heroVideo.metadata('videoPosition').value() || null;

  // breadCrumb
  const breadcrumbTitle = heroVideo.metadata('breadcrumbTitle').value();
  const pageTitleToUse = (breadcrumbTitle || title).replace('Electric', 'EV').trim();
  const breadcrumbsData = buildArticlePageBreadcrumb(year, pageTitleToUse, pathname);

  const mainEntryData = mainEntries.reduce((acc, cur) => {
    if (cur.id === 'vehicle-details') {
      acc.vehicleDetails = cur;
    } else if (cur.id === 'vehicle-tiles') {
      acc.vehicleTiles = cur.links();
    } else if (cur.id === 'winners-list') {
      acc.winnersList = cur;
    } else if (cur.id === 'config') {
      acc.config = cur;
    } else if (cur.id === 'featured-video-images') {
      const imageLinks = cur.links();
      const videoId = cur.metadata('videoId').value();
      const videoTitle = cur.metadata('videoTitle').value();
      const href = cur.metadata('videoThumbnailURL').value();
      const videoThumbOnly = cur.metadata('videoThumbnailOnly').value() === 'true';
      const videoAspectRatio = cur.metadata('videoThumbnailAspectRatio').value();
      acc.featuredVideoImages = [
        { videoId, videoTitle, href, title: videoTitle, videoThumbOnly, videoAspectRatio },
        ...imageLinks,
      ];
    } else if (cur.id === 'by-the-numbers') {
      acc.byTheNumbers = cur;
    }

    return acc;
  }, {});

  const vehicleDetails = mainEntryData.vehicleDetails || null;
  const vehicleTitle = vehicleDetails && vehicleDetails.metadata('vehicletitle').value();
  const vehicleSubtitle = vehicleDetails && vehicleDetails.metadata('subtitle').value();
  const vehicleDate = vehicleDetails && vehicleDetails.metadata('date').value();
  const coreLink = vehicleDetails && vehicleDetails.metadata('coreLink').value();
  const coreLinkText = vehicleDetails && vehicleDetails.metadata('coreLinkText').value();

  const vehicleTiles = mainEntryData.vehicleTiles || null;
  const vehicleGallery = vehicleTiles && vehicleTiles.map(tile => ({ href: tile.href, title: tile.captionTranscript }));

  const winnersList = mainEntryData.winnersList || null;
  const winners = winnersList && winnersList.child('winners');
  const winnerTitle = winners && winners.metadata('winnerTitle').value();
  const winnerVehicles = winners && winners.children();
  const permissions = mainEntryData.config && mainEntryData.config.child('permissions');
  const fullImageSize = mainEntryData.config && mainEntryData.config.child('fullImageSize');
  const platform = isMobile ? 'mobile' : 'wired';
  const fullImageSizeFromMetadata = fullImageSize && fullImageSize.metadata(platform).value();
  const winnersEntries = winnerVehicles;

  return (
    <Fragment>
      <figure className="top-rated-article hero m-0" style={cssVideoProps}>
        <Breadcrumbs className="px-0" data={breadcrumbsData} />
        {hasHeroVideo ? (
          <HighlightVideo entry={heroVideo} openVideoModal={onToggleVideoModal} videoAspectRatio="2.35:1" />
        ) : (
          <HeroContent
            className="hero-image h-100"
            entry={heroVideo}
            alt={title}
            heroSize={isMobile ? HERO_PHOTO_SIZE.MOBILE : HERO_PHOTO_SIZE.DESKTOP}
            isMobile={isMobile}
            fetchpriority="high"
          />
        )}
      </figure>
      <section className="top-rated-article article-wrapper mx-1">
        <article className="gradient-wrapper article-top mx-auto" id="main-content" tabIndex={-1}>
          <section className="pt-1_5 pt-md-2_5 px-0_75 pb-3 pb-md-3_5 text-center">
            <header className="text-center">
              <h1 className="heading-7 mb-0_25 mb-md-1_25 text-primary" style={{ WebkitBackgroundClip: 'text' }}>
                {title}
              </h1>
              <h2
                className={classnames('heading-3 mb-0_5 mb-md-1_25', topRatedHelpers.swapTextColor('text-white', year))}
              >
                {vehicleTitle}
              </h2>
              {vehicleSubtitle && (
                <ContentFragment
                  classes={classnames(
                    'size-16 mb-0_5 mx-auto vehicle-subtitle',
                    topRatedHelpers.swapTextColor('text-white', year)
                  )}
                >
                  {vehicleSubtitle}
                </ContentFragment>
              )}
              <div
                className={classnames(
                  'size-12 mb-md-0_75 vehicle-date',
                  topRatedHelpers.swapTextColor('text-white', year)
                )}
              >
                {dateFormat(vehicleDate || updated || published, 'mmmm dS, yyyy')}
              </div>
              <Share
                url={canonical}
                title={title}
                labelClassName="d-none"
                containerClassName="d-block text-center my-1_75"
                iconClassName={classnames('heading-4 mx-1 p-0', topRatedHelpers.swapTextColor('text-white', year))}
              />
            </header>
            <MainContent entries={mainEntries} isMobile={isMobile} />
            <a className="cta-core-link mt-0_75 btn btn-primary-b mt-md-1 fw-normal size-16" href={coreLink}>
              {coreLinkText}
            </a>
          </section>
          {vehicleGallery && (
            <PhotoflipperImageTopRated vehicleGallery={vehicleGallery} fullImageSize={fullImageSizeFromMetadata} />
          )}
        </article>
        <WinnerTilesNavArticle winnerVehicles={winnersEntries} year={year} winnerTitle={winnerTitle} title={title} />

        {permissions && (
          <AwardsAccolades
            title={permissions.metadata('title').value()}
            content={permissions.metadata('content').value()}
            mailTo={permissions.metadata('mailTo').value()}
            email={permissions.metadata('email').value()}
            themeToUse={themeToUse}
          />
        )}
      </section>
      <YoutubeVideoModalAsync
        onToggleModal={onToggleVideoModal}
        isOpen={isVideoModalOpen}
        videoId={videoModalData.videoId}
        creativeId={videoModalData.creativeId}
        videoTitle={videoModalData.videoTitle}
        videoAspectRatio={videoModalData.videoAspectRatio}
      />
    </Fragment>
  );
}

TopRatedArticleUI.propTypes = {
  content: CmsEntities.Content.isRequired,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  year: PropTypes.string.isRequired,
  themeContent: CmsEntities.Content,
};

export const TopRatedArticle = rendererTopRatedArticle(TopRatedArticleUI);
